import { Flex, Input, SegmentedControl, Select, Text } from '@byecode/ui'
import {
    TIMER_INTERVAL_UNIT,
    TIMER_TRIGGER_MODE,
    type TimerEventConfig as TimerEventConfigType,
    type TimerEventProtocol,
    type TimerIntervalPreset
} from '@lighthouse/core'
import {
    formatTimerEventLabel,
    ListItem4ByecodeUi,
    PopoverDropdownWithClose,
    TIMER_INTERVAL_PRESET_OPTIONS,
    TIMER_INTERVAL_UNIT_OPTIONS,
    TIMER_TRIGGER_MODE_OPTIONS
} from '@lighthouse/shared'
import React, { useCallback } from 'react'

import { transform2Number } from '@/utils/number'

interface TimerEventConfigProps {
    value: TimerEventProtocol
    onChange: (value: TimerEventProtocol) => void
}

const patchConfig = (value: TimerEventProtocol, patch: Partial<TimerEventConfigType>): TimerEventProtocol => ({
    ...value,
    config: {
        ...value.config,
        ...patch
    }
})

export const TimerEventConfig: React.FC<TimerEventConfigProps> = ({ value, onChange }) => {
    const { config } = value
    const triggerMode = config.triggerMode ?? TIMER_TRIGGER_MODE.ONCE
    const intervalPreset = config.intervalPreset ?? '5s'
    const isCustom = intervalPreset === 'CUSTOM'
    const intervalLabel = triggerMode === TIMER_TRIGGER_MODE.REPEAT ? '重复间隔' : '延迟时间'

    const handleTriggerModeChange = useCallback(
        (mode: string) => {
            onChange(patchConfig(value, { triggerMode: mode as TIMER_TRIGGER_MODE }))
        },
        [onChange, value]
    )

    const handlePresetChange = useCallback(
        (preset: string) => {
            onChange(
                patchConfig(value, {
                    intervalPreset: preset as TimerIntervalPreset,
                    customInterval: value.config.customInterval ?? 1,
                    customIntervalUnit: value.config.customIntervalUnit ?? TIMER_INTERVAL_UNIT.SECOND
                })
            )
        },
        [onChange, value]
    )

    const handleCustomIntervalChange = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const num = ev.target.value ? transform2Number(ev.target.value) : undefined
            onChange(patchConfig(value, { customInterval: num }))
        },
        [onChange, value]
    )

    const handleCustomIntervalBlur = useCallback(() => {
        const current = value.config.customInterval
        if (current === undefined || current === null || !Number.isFinite(current) || current <= 0) {
            onChange(patchConfig(value, { customInterval: 1 }))
        }
    }, [onChange, value])

    const handleUnitChange = useCallback(
        (unit: string) => {
            onChange(patchConfig(value, { customIntervalUnit: unit as TIMER_INTERVAL_UNIT }))
        },
        [onChange, value]
    )

    return (
        <PopoverDropdownWithClose title={formatTimerEventLabel(config)}>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>触发方式</Text>
                <SegmentedControl
                    style={{ width: 180 }}
                    data={TIMER_TRIGGER_MODE_OPTIONS}
                    value={triggerMode}
                    onChange={handleTriggerModeChange}
                    fullWidth
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>{intervalLabel}</Text>
                <Select
                    style={{ width: 180 }}
                    options={TIMER_INTERVAL_PRESET_OPTIONS}
                    value={intervalPreset}
                    onChange={handlePresetChange}
                />
            </ListItem4ByecodeUi>
            {isCustom && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>自定义时间</Text>
                    <Flex alignItems="center" gap={8} style={{ width: 180 }}>
                        <Input
                            style={{ flex: 1 }}
                            type="number"
                            min={1}
                            value={config.customInterval ?? ''}
                            onChange={handleCustomIntervalChange}
                            onKeyDownCapture={ev => {
                                if (ev.key === 'Enter') {
                                    ev.currentTarget.blur()
                                }
                            }}
                            onBlur={handleCustomIntervalBlur}
                        />
                        <Select
                            style={{ width: 72 }}
                            options={TIMER_INTERVAL_UNIT_OPTIONS}
                            value={config.customIntervalUnit ?? TIMER_INTERVAL_UNIT.SECOND}
                            onChange={handleUnitChange}
                        />
                    </Flex>
                </ListItem4ByecodeUi>
            )}
        </PopoverDropdownWithClose>
    )
}
